import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import useCSVHandler from '../../../../../shared/hooks/UseCSVHandler';
import { getSparkBaseUrl } from '../../../../../shared/constants/Urls';
import { CallDataRecord, sortDataRecords } from './dashboardTypes';
import { useCallDataModalFilters, SupportedDomain } from '../hooks/useDataModalFilters';
import { getCategoryName } from '../../../../../shared/constants/analyticsConstants/generativeCallReview';
import { useTagConfig } from '../../../../../shared/hooks/TagConfigContainer';
import DeduplicatedTagsList from './DeduplicatedTagsList';
import { color_shades_dark } from '../../../../../shared/constants/colors';
import { canViewInternalVoicifyTags, filterInternalVoicifyTags, isInternalVoicifyTag } from '../../../../../shared/utils/tagVisibility';
import {
    CallDataModalContent,
    CallDataModalHeader,
    CallDataModalTitleSection,
    CallDataModalTitle,
    CallDataDateRange,
    CallDataModalCloseButton,
    CallDataScrollContainer,
    CallDataTableEmptyOverlay,
    CallDataTable,
    CallDataTh,
    CallDataRow,
    CallDataTd,
    TranscriptLink,
    TagsContainer,
    CallTag,
    CallDataModalFooter,
    ExportInfoText,
    ExportButton,
    ButtonSpinner,
    SearchInputWrapper,
    FilterSearchInput,
    ClearSearchButton,
    ClearFilterLink,
    FilterActionsContainer,
    ResultsInfo,
    FilterableColumnHeader,
    ColumnHeaderContent,
    ColumnHeaderText,
    ColumnFilterIcon,
    SortIcon,
    ColumnFilterDropdown,
    ColumnFilterHeader,
    ColumnFilterContent,
    ColumnFilterItem,
    ColumnFilterCheckbox,
    PhoneFilterSection,
    PhoneFilterModeToggle,
    PhoneFilterModeButton,
    PhoneFilterList,
    PhoneFilterTag,
    PhoneFilterTagRemove,
    SimpleFilterControlsContainer,
    SimpleFilterRow,
    TruncatedLocationText,
    CategoryBadge,
    TagFilterModeToggle,
    TagFilterModeButton
} from './ApplicationDashboard.styles';

const filterIcon = require('../../../../../shared/content/images/spark/filter.svg');

export interface CallDataModalProps {
    isVisible: boolean;
    onClose: () => void;
    title: string;
    startDate: string;
    endDate: string;
    records: CallDataRecord[];
    domain: SupportedDomain;
    /** Show the Category column (default: true) */
    showCategoryColumn?: boolean;
    /** Show the Tags column (default: true) */
    showTagsColumn?: boolean;
    /** Custom export filename prefix */
    exportFileName?: string;
    /** Add an ES badge for Spanish-only filter mode */
    spanishOnlyFilter?: boolean;
    /** Custom subtitle text below title */
    subtitle?: string;
    /** Show an order total threshold filter for placed orders */
    enableOrderTotalThresholdFilter?: boolean;
    /** Use only raw tags present in records for filter options */
    useRecordTagsOnly?: boolean;
}

/**
 * CallDataModal - A reusable modal component for displaying call data tables
 * 
 * Features:
 * - Search across all fields
 * - Filter by phone (include/exclude mode), location, category, tags
 * - Sort by date, time, phone, location, category
 * - Pagination with 100 records per page
 * - Export to CSV
 */
const CallDataModal: React.FC<CallDataModalProps> = ({
    isVisible,
    onClose,
    title,
    startDate,
    endDate,
    records,
    domain,
    showCategoryColumn = true,
    showTagsColumn = true,
    exportFileName,
    spanishOnlyFilter = false,
    subtitle,
    enableOrderTotalThresholdFilter = false,
    useRecordTagsOnly = false
}) => {
    const { getTagName, foodOrderingTags, healthcareTags, outboundCallingTags, loadTagConfig } = useTagConfig();
    const csvHandler = useCSVHandler();
    const showInternalTags = canViewInternalVoicifyTags();

    const getCallRowKey = useCallback((call: CallDataRecord, index: number) => {
        return [
            call.id,
            call.sessionId,
            call.applicationId,
            call.date,
            call.time,
            call.category,
            call.tags.join('|'),
            index
        ].join('::');
    }, []);
    const [isExporting, setIsExporting] = React.useState(false);
    const [orderTotalFilterMode, setOrderTotalFilterMode] = useState<'above' | 'below'>('above');
    const [orderTotalThreshold, setOrderTotalThreshold] = useState('');

    // Load tag config from the first record's applicationId
    useEffect(() => {
        if (records?.length > 0 && records[0].applicationId) {
            loadTagConfig(records[0].applicationId);
        }
    }, [records]);

    // Determine which tag set to use based on domain and the current user's internal-tag access.
    // Merge in any tags from the actual data that aren't in the S3 config so that
    // server-side tags (e.g. TRANSFERRED_BUT_CONTAINED) are always filterable.
    const tagSet = useMemo(() => {
        if (useRecordTagsOnly) {
            return [...new Set(
                (records || []).flatMap(record =>
                    filterInternalVoicifyTags(record.tags || [], showInternalTags)
                )
            )].sort();
        }

        const baseTags = domain === 'FOOD_ORDERING'
            ? foodOrderingTags
            : domain === 'OUTBOUND_CALLING'
                ? outboundCallingTags
                : healthcareTags;
        const configTags = filterInternalVoicifyTags(baseTags, showInternalTags);
        const configTagSet = new Set(configTags);
        // Collect tags present in data but missing from the config
        const extraTags = new Set<string>();
        records?.forEach(record => {
            record.tags?.forEach(tag => {
                if ((showInternalTags || !isInternalVoicifyTag(tag)) && !configTagSet.has(tag)) {
                    extraTags.add(tag);
                }
            });
        });
        // Config tags first (preserves intended ordering), then extras sorted alphabetically
        return [...configTags, ...[...extraTags].sort()];
    }, [domain, foodOrderingTags, healthcareTags, outboundCallingTags, records, useRecordTagsOnly, showInternalTags]);

    const parsedOrderTotalThreshold = useMemo(() => {
        if (!orderTotalThreshold.trim()) return null;
        const parsed = Number(orderTotalThreshold);
        return Number.isFinite(parsed) ? parsed : null;
    }, [orderTotalThreshold]);

    const hasActiveOrderTotalFilter = enableOrderTotalThresholdFilter && parsedOrderTotalThreshold !== null;

    const thresholdFilteredRecords = useMemo(() => {
        if (!hasActiveOrderTotalFilter || parsedOrderTotalThreshold === null) return records;

        return records.filter(call => {
            if (typeof call.orderTotal !== 'number') return false;
            return orderTotalFilterMode === 'above'
                ? call.orderTotal > parsedOrderTotalThreshold
                : call.orderTotal < parsedOrderTotalThreshold;
        });
    }, [records, hasActiveOrderTotalFilter, parsedOrderTotalThreshold, orderTotalFilterMode]);

    // Use the filter hook
    const filters = useCallDataModalFilters(
        thresholdFilteredRecords,
        isVisible,
        getTagName,
        domain
    );

    const clearAllFilters = useCallback(() => {
        filters.clearAllFilters();
        setOrderTotalFilterMode('above');
        setOrderTotalThreshold('');
    }, [filters]);

    const hasAnyActiveFilters = filters.hasActiveFilters || hasActiveOrderTotalFilter;

    // Handle close with filter reset
    const handleClose = useCallback(() => {
        clearAllFilters();
        onClose();
    }, [clearAllFilters, onClose]);

    // Export to CSV
    const handleExportCSV = useCallback(() => {
        setIsExporting(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = tagSet.map(tag => getTagName(tag, domain));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data for export
                let dataToExport = thresholdFilteredRecords;
                if (filters.hasActiveFilters) {
                    dataToExport = thresholdFilteredRecords.filter(call => {
                        const searchLower = filters.searchQuery.toLowerCase();
                        const matchesSearch = !filters.searchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.category.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, domain).toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (filters.selectedPhones.size > 0) {
                            matchesPhone = filters.phoneFilterMode === 'include'
                                ? filters.selectedPhones.has(call.phoneLastFour)
                                : !filters.selectedPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = filters.selectedLocations.size === 0 || filters.selectedLocations.has(call.location);
                        const matchesTags = filters.selectedTags.size === 0 || (
                            filters.tagFilterMode === 'any'
                                ? call.tags.some(tag => filters.selectedTags.has(tag))
                                : [...filters.selectedTags].every(tag => call.tags.includes(tag))
                        );
                        const notExcluded = filters.excludedTags.size === 0 || !call.tags.some(tag => filters.excludedTags.has(tag));
                        const matchesCategory = filters.selectedCategories.size === 0 || filters.selectedCategories.has(call.category);

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags && notExcluded && matchesCategory;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, filters.sortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const visibleTags = filterInternalVoicifyTags(call.tags, showInternalTags);
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                        'Category': call.category,
                        'All Tags': visibleTags.map(tag => getTagName(tag, domain)).join('; ')
                    };

                    tagSet.forEach(tag => {
                        const tagDisplayName = getTagName(tag, domain);
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasAnyActiveFilters ? '-filtered' : '';
                const prefix = exportFileName || 'all-calls';
                const filename = `${prefix}-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExporting(false);
            }
        }, 50);
    }, [thresholdFilteredRecords, filters, tagSet, domain, exportFileName, startDate, endDate, csvHandler, hasAnyActiveFilters, showInternalTags]);

    // Calculate column span based on visible columns
    const columnCount = 5 + (showCategoryColumn ? 1 : 0) + (showTagsColumn ? 1 : 0);

    return (
        <ModalLayout isVisible={isVisible} onClose={handleClose} zIndex={1000}>
            <CallDataModalContent>
                <CallDataModalHeader>
                    <CallDataModalTitleSection>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <CallDataModalTitle>{title}</CallDataModalTitle>
                            {spanishOnlyFilter && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#2E566E',
                                    color: 'white',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    letterSpacing: '0.5px'
                                }}>ES</span>
                            )}
                        </div>
                        <CallDataDateRange>
                            {subtitle || `${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}`}
                        </CallDataDateRange>
                    </CallDataModalTitleSection>
                    <CallDataModalCloseButton onClick={handleClose}>×</CallDataModalCloseButton>
                </CallDataModalHeader>

                <SimpleFilterControlsContainer>
                    <SimpleFilterRow>
                        <SearchInputWrapper>
                            <FilterSearchInput
                                type="text"
                                placeholder="Search all fields..."
                                value={filters.searchQuery}
                                onChange={(e) => filters.setSearchQuery(e.target.value)}
                            />
                            {filters.searchQuery && (
                                <ClearSearchButton onClick={() => filters.setSearchQuery('')}>×</ClearSearchButton>
                            )}
                        </SearchInputWrapper>
                        <ResultsInfo style={{ margin: 0 }}>
                            Showing <strong>{filters.filteredData.length}</strong> of <strong>{records.length}</strong> calls
                            {hasAnyActiveFilters && (
                                <> (filtered) <ClearFilterLink onClick={clearAllFilters}>Clear all</ClearFilterLink></>
                            )}
                        </ResultsInfo>
                    </SimpleFilterRow>
                    {enableOrderTotalThresholdFilter && (
                        <SimpleFilterRow style={{ marginTop: '12px', alignItems: 'flex-end' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: color_shades_dark }}>Order total</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <select
                                        value={orderTotalFilterMode}
                                        onChange={(e) => setOrderTotalFilterMode(e.target.value as 'above' | 'below')}
                                        style={{
                                            height: '38px',
                                            border: '1px solid #D7DDE3',
                                            borderRadius: '8px',
                                            padding: '0 12px',
                                            fontSize: '13px',
                                            color: '#1F2933',
                                            background: 'white',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="above">Above</option>
                                        <option value="below">Below</option>
                                    </select>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: 'white',
                                        border: '1px solid #D7DDE3',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        minWidth: '160px'
                                    }}>
                                        <span style={{ fontSize: '13px', color: color_shades_dark, marginRight: '6px' }}>$</span>
                                        <FilterSearchInput
                                            type="number"
                                            inputMode="decimal"
                                            min="0"
                                            step="0.01"
                                            placeholder="Threshold"
                                            value={orderTotalThreshold}
                                            onChange={(e) => setOrderTotalThreshold(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {hasActiveOrderTotalFilter && parsedOrderTotalThreshold !== null && (
                                <ResultsInfo style={{ margin: 0 }}>
                                    Filtering to calls {orderTotalFilterMode} <strong>${parsedOrderTotalThreshold.toFixed(2)}</strong>
                                </ResultsInfo>
                            )}
                        </SimpleFilterRow>
                    )}
                </SimpleFilterControlsContainer>

                <CallDataScrollContainer>
                    <CallDataTable>
                        <thead>
                            <tr>
                                {/* Date Column */}
                                <FilterableColumnHeader hasActiveFilter={filters.sortConfig?.key === 'date'}>
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Date</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'date'} onClick={() => filters.handleSort('date')} title="Sort">
                                            {filters.sortConfig?.key === 'date' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                    </ColumnHeaderContent>
                                </FilterableColumnHeader>

                                {/* Time Column */}
                                <FilterableColumnHeader hasActiveFilter={filters.sortConfig?.key === 'time'}>
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Time</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'time'} onClick={() => filters.handleSort('time')} title="Sort">
                                            {filters.sortConfig?.key === 'time' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                    </ColumnHeaderContent>
                                </FilterableColumnHeader>

                                {/* Phone Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedPhones.size > 0 || filters.sortConfig?.key === 'phone'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Phone (Last 4)</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'phone'} onClick={() => filters.handleSort('phone')} title="Sort">
                                            {filters.sortConfig?.key === 'phone' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedPhones.size > 0}
                                            isOpen={filters.openColumnFilter === 'phone'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'phone' ? null : 'phone'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'phone' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter Phone Numbers</span>
                                                <FilterActionsContainer>
                                                    <ClearFilterLink onClick={() => filters.setSelectedPhones(new Set(filters.uniquePhones))}>Select All</ClearFilterLink>
                                                    <ClearFilterLink onClick={() => filters.setSelectedPhones(new Set())}>Clear</ClearFilterLink>
                                                </FilterActionsContainer>
                                            </ColumnFilterHeader>
                                            <PhoneFilterSection>
                                                <PhoneFilterModeToggle>
                                                    <PhoneFilterModeButton isActive={filters.phoneFilterMode === 'include'} mode="include" onClick={() => filters.setPhoneFilterMode('include')}>Include</PhoneFilterModeButton>
                                                    <PhoneFilterModeButton isActive={filters.phoneFilterMode === 'exclude'} mode="exclude" onClick={() => filters.setPhoneFilterMode('exclude')}>Exclude</PhoneFilterModeButton>
                                                </PhoneFilterModeToggle>
                                                {filters.selectedPhones.size > 0 && (
                                                    <PhoneFilterList>
                                                        {[...filters.selectedPhones].map(phone => (
                                                            <PhoneFilterTag key={phone} isExclude={filters.phoneFilterMode === 'exclude'}>
                                                                ***{phone}
                                                                <PhoneFilterTagRemove onClick={() => filters.handlePhoneToggle(phone)}>×</PhoneFilterTagRemove>
                                                            </PhoneFilterTag>
                                                        ))}
                                                    </PhoneFilterList>
                                                )}
                                            </PhoneFilterSection>
                                            <ColumnFilterContent>
                                                {filters.uniquePhones.map(phone => (
                                                    <ColumnFilterItem key={phone} onClick={() => filters.handlePhoneToggle(phone)}>
                                                        <ColumnFilterCheckbox state={filters.selectedPhones.has(phone) ? 'checked' : 'unchecked'} />
                                                        <span>***{phone}</span>
                                                    </ColumnFilterItem>
                                                ))}
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>

                                {/* Location Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedLocations.size > 0 || filters.sortConfig?.key === 'location'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Location</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'location'} onClick={() => filters.handleSort('location')} title="Sort">
                                            {filters.sortConfig?.key === 'location' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedLocations.size > 0}
                                            isOpen={filters.openColumnFilter === 'location'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'location' ? null : 'location'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'location' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter by Location</span>
                                                <FilterActionsContainer>
                                                    <ClearFilterLink onClick={() => filters.setSelectedLocations(new Set(filters.uniqueLocations))}>Select All</ClearFilterLink>
                                                    <ClearFilterLink onClick={() => filters.setSelectedLocations(new Set())}>Clear</ClearFilterLink>
                                                </FilterActionsContainer>
                                            </ColumnFilterHeader>
                                            <ColumnFilterContent>
                                                {filters.uniqueLocations.map(location => (
                                                    <ColumnFilterItem key={location} onClick={() => {
                                                        const newSet = new Set(filters.selectedLocations);
                                                        if (newSet.has(location)) newSet.delete(location);
                                                        else newSet.add(location);
                                                        filters.setSelectedLocations(newSet);
                                                    }}>
                                                        <ColumnFilterCheckbox state={filters.selectedLocations.has(location) ? 'checked' : 'unchecked'} />
                                                        <span>{location}</span>
                                                    </ColumnFilterItem>
                                                ))}
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>

                                {/* Transcript Column */}
                                <CallDataTh>Transcript</CallDataTh>

                                {/* Category Column */}
                                {showCategoryColumn && (
                                    <FilterableColumnHeader
                                        hasActiveFilter={filters.selectedCategories.size > 0 || filters.sortConfig?.key === 'category'}
                                        tabIndex={-1}
                                        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                    >
                                        <ColumnHeaderContent>
                                            <ColumnHeaderText>Category</ColumnHeaderText>
                                            <SortIcon isActive={filters.sortConfig?.key === 'category'} onClick={() => filters.handleSort('category')} title="Sort">
                                                {filters.sortConfig?.key === 'category' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                            </SortIcon>
                                            <ColumnFilterIcon
                                                isActive={filters.selectedCategories.size > 0}
                                                isOpen={filters.openColumnFilter === 'category'}
                                                onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'category' ? null : 'category'); }}
                                                title="Filter"
                                            >
                                                <img src={filterIcon} alt="Filter" />
                                            </ColumnFilterIcon>
                                        </ColumnHeaderContent>
                                        {filters.openColumnFilter === 'category' && (
                                            <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                <ColumnFilterHeader>
                                                    <span>Filter by Category</span>
                                                    <FilterActionsContainer>
                                                        <ClearFilterLink onClick={() => filters.setSelectedCategories(new Set(filters.uniqueCategories))}>Select All</ClearFilterLink>
                                                        <ClearFilterLink onClick={() => filters.setSelectedCategories(new Set())}>Clear</ClearFilterLink>
                                                    </FilterActionsContainer>
                                                </ColumnFilterHeader>
                                                <ColumnFilterContent>
                                                    {filters.uniqueCategories.map(category => (
                                                        <ColumnFilterItem key={category} onClick={() => {
                                                            const newSet = new Set(filters.selectedCategories);
                                                            if (newSet.has(category)) newSet.delete(category);
                                                            else newSet.add(category);
                                                            filters.setSelectedCategories(newSet);
                                                        }}>
                                                            <ColumnFilterCheckbox state={filters.selectedCategories.has(category) ? 'checked' : 'unchecked'} />
                                                            <span>{getCategoryName(category, domain)}</span>
                                                        </ColumnFilterItem>
                                                    ))}
                                                </ColumnFilterContent>
                                            </ColumnFilterDropdown>
                                        )}
                                    </FilterableColumnHeader>
                                )}

                                {/* Tags Column */}
                                {showTagsColumn && (
                                    <FilterableColumnHeader
                                        hasActiveFilter={filters.selectedTags.size > 0 || filters.excludedTags.size > 0}
                                        tabIndex={-1}
                                        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                        style={{ minWidth: 'unset' }}
                                    >
                                        <ColumnHeaderContent>
                                            <ColumnHeaderText>Tags</ColumnHeaderText>
                                            <ColumnFilterIcon
                                                isActive={filters.selectedTags.size > 0 || filters.excludedTags.size > 0}
                                                isOpen={filters.openColumnFilter === 'tags'}
                                                onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'tags' ? null : 'tags'); }}
                                                title="Filter"
                                            >
                                                <img src={filterIcon} alt="Filter" />
                                            </ColumnFilterIcon>
                                        </ColumnHeaderContent>
                                        {filters.openColumnFilter === 'tags' && (
                                            <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                                <ColumnFilterHeader>
                                                    <span>Filter by Tags</span>
                                                    <FilterActionsContainer>
                                                        <ClearFilterLink onClick={() => filters.setSelectedTags(new Set(tagSet))}>Select All</ClearFilterLink>
                                                        <ClearFilterLink onClick={() => { filters.setSelectedTags(new Set()); filters.setExcludedTags(new Set()); }}>Clear</ClearFilterLink>
                                                    </FilterActionsContainer>
                                                </ColumnFilterHeader>
                                                <TagFilterModeToggle>
                                                    <TagFilterModeButton isActive={filters.tagFilterMode === 'any'} onClick={() => filters.setTagFilterMode('any')}>Any</TagFilterModeButton>
                                                    <TagFilterModeButton isActive={filters.tagFilterMode === 'all'} onClick={() => filters.setTagFilterMode('all')}>All</TagFilterModeButton>
                                                </TagFilterModeToggle>
                                                <ColumnFilterContent>
                                                    {tagSet.map(tag => (
                                                        <ColumnFilterItem key={tag} onClick={() => {
                                                            if (filters.excludedTags.has(tag)) {
                                                                // excluded -> unchecked
                                                                const newExcluded = new Set(filters.excludedTags);
                                                                newExcluded.delete(tag);
                                                                filters.setExcludedTags(newExcluded);
                                                            } else if (filters.selectedTags.has(tag)) {
                                                                // checked -> excluded
                                                                const newSelected = new Set(filters.selectedTags);
                                                                newSelected.delete(tag);
                                                                filters.setSelectedTags(newSelected);
                                                                const newExcluded = new Set(filters.excludedTags);
                                                                newExcluded.add(tag);
                                                                filters.setExcludedTags(newExcluded);
                                                            } else {
                                                                // unchecked -> checked
                                                                const newSelected = new Set(filters.selectedTags);
                                                                newSelected.add(tag);
                                                                filters.setSelectedTags(newSelected);
                                                            }
                                                        }}>
                                                            <ColumnFilterCheckbox state={filters.excludedTags.has(tag) ? 'excluded' : filters.selectedTags.has(tag) ? 'checked' : 'unchecked'} />
                                                            <span>{getTagName(tag, domain)}</span>
                                                        </ColumnFilterItem>
                                                    ))}
                                                </ColumnFilterContent>
                                            </ColumnFilterDropdown>
                                        )}
                                    </FilterableColumnHeader>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filters.paginatedData.map((call, index) => (
                                <CallDataRow key={getCallRowKey(call, index)}>
                                    <CallDataTd>{call.date}</CallDataTd>
                                    <CallDataTd>{call.time}</CallDataTd>
                                    <CallDataTd>***{call.phoneLastFour}</CallDataTd>
                                    <CallDataTd><TruncatedLocationText title={call.location}>{call.location}</TruncatedLocationText></CallDataTd>
                                    <CallDataTd>
                                        <TranscriptLink href={`/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`} target="_blank" rel="noopener noreferrer">
                                            View Transcript
                                        </TranscriptLink>
                                    </CallDataTd>
                                    {showCategoryColumn && (
                                        <CallDataTd>
                                            <CategoryBadge category={call.category}>{getCategoryName(call.category, domain)}</CategoryBadge>
                                        </CallDataTd>
                                    )}
                                    {showTagsColumn && (
                                        <CallDataTd>
                                            <DeduplicatedTagsList tags={call.tags} domain={domain} />
                                        </CallDataTd>
                                    )}
                                </CallDataRow>
                            ))}
                        </tbody>
                    </CallDataTable>
                    {filters.filteredData.length === 0 && (
                        <CallDataTableEmptyOverlay>
                            No calls match your current filters.
                        </CallDataTableEmptyOverlay>
                    )}
                </CallDataScrollContainer>

                <CallDataModalFooter>
                    {filters.totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
                            <button
                                onClick={() => filters.setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={filters.currentPage === 0}
                                style={{ padding: '4px 8px', cursor: filters.currentPage === 0 ? 'not-allowed' : 'pointer', opacity: filters.currentPage === 0 ? 0.5 : 1 }}
                            >
                                ← Prev
                            </button>
                            <span style={{ fontSize: '13px', color: color_shades_dark }}>
                                Page {filters.currentPage + 1} of {filters.totalPages}
                            </span>
                            <button
                                onClick={() => filters.setCurrentPage(p => Math.min(filters.totalPages - 1, p + 1))}
                                disabled={filters.currentPage >= filters.totalPages - 1}
                                style={{ padding: '4px 8px', cursor: filters.currentPage >= filters.totalPages - 1 ? 'not-allowed' : 'pointer', opacity: filters.currentPage >= filters.totalPages - 1 ? 0.5 : 1 }}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                    <ExportInfoText>
                        {hasAnyActiveFilters
                            ? `Exporting ${filters.filteredData.length} filtered results`
                            : `Exporting all ${records.length} results`}
                    </ExportInfoText>
                    <ExportButton onClick={handleExportCSV} disabled={isExporting || records.length === 0}>
                        {isExporting && <ButtonSpinner />}
                        {isExporting ? 'Exporting...' : 'Export to CSV'}
                    </ExportButton>
                </CallDataModalFooter>
            </CallDataModalContent>
        </ModalLayout>
    );
};

export default CallDataModal;
